export const ROUTES = {
  CONTEXT_EXPLORER: "/context-explorer",
  AUTH_LOGIN: "/auth/login",
  AUTH_OAUTH: "/auth/oauth",
  AUTH_OIDC: "/auth/oidc",
  AUTH_SAML: "/auth/saml",
  AUTH_FORGOT_PASSWORD: "/auth/forgot-password",
  AUTH_RESET_PASSWORD: "/auth/reset-password",
  AUTH_SIGNUP: "/auth/signup",
  AUTH_VERIFY_EMAIL: "/auth/verify-email",
  AUTH_WAITING_VERIFICATION: "/auth/waiting-on-verification",
  AUTH_GIT_CALLBACK: "/auth/git/callback",

  APP_HOME: "/",
  APP_AGENTS: "/agents",
  APP_AGENTS_CREATE: "/agents/create",
  APP_AGENT_EDIT: (id: string | number) => `/agents/${id}/edit`,
  APP_SETTINGS: "/settings",
  SETTINGS_GIT_ACCOUNTS: "/settings/git-accounts",
  APP_PROJECTS: "/projects",
  APP_GRAPH: "/graph",
  APP_SHARED_CHAT: (id: string | number) => `/shared/${id}`,
  // TASK-EU003-002 (BUG-EU003-002) — Slack/Discord account-linking
  // confirmation page. NOT nested under the "/" ProtectedRoute tree (see
  // router/index.tsx comment) — needs its own auth redirect that preserves
  // `?token=`.
  APP_CONNECT_BOT: "/connect-bot",

  // ─── Workflows (v9) ──────────────────────────────────────────────────────
  WORKFLOWS: "/workflows",
  WORKFLOWS_NEW: "/workflows/new",
  WORKFLOWS_TEMPLATES: "/workflows/templates",
  WORKFLOW_DETAIL: (id: string | number) => `/workflows/${id}`,
  WORKFLOW_EDIT: (id: string | number) => `/workflows/${id}/edit`,
  WORKFLOW_EXECUTION_DETAIL: (id: string | number, execId: string | number) => `/workflows/${id}/executions/${execId}`,
  WORKFLOW_SETTINGS: (id: string | number) => `/workflows/${id}/settings`,
  ADMIN_WORKFLOWS_DLQ: "/admin/workflows/dlq",

  ADMIN_AGENTS: "/admin/agents",
  ADMIN_DOC_AGENTS: "/admin/doc-agents",
  ADMIN_MCP_AGENTS: "/admin/mcp-agents",
  ADMIN_MCP_AGENTS_ADD: "/admin/mcp-agents/add",
  ADMIN_MCP_AGENTS_TOOLS: "/admin/mcp-agents/tools",
  ADMIN_MCP_AGENTS_LOGS: "/admin/mcp-agents/logs",
  // CR-AGENTSCHED-WP-002 — "Run Tool Now" ad-hoc invoke page.
  ADMIN_MCP_AGENTS_ADHOC_INVOKE: "/admin/mcp-agents/adhoc-invoke",
  ADMIN_MCP_AGENTS_DETAIL: (id: string) => `/admin/mcp-agents/${id}`,
  // MCP-03 (External MCP Consumer) & OpenAPI Actions admin pages.
  // These routes existed as sidebar items in AdminSidebar.tsx but were
  // never registered in routesConfig.tsx — now fixed.
  ADMIN_ACTIONS_MCP: "/admin/actions/mcp",
  ADMIN_ACTIONS_OPENAPI: "/admin/actions/open-api",
  // BUG-DEV002-002 — per-tool MCP access rule overview, so an admin doesn't
  // have to open every server's card on ADMIN_ACTIONS_MCP to find a tool.
  ADMIN_TOOL_PERMISSIONS: "/admin/tool-permissions",

  ADMIN_OPENWORK: "/admin/openwork",
  ADMIN_OPENWORK_CONFIG: "/admin/openwork/config",
  ADMIN_SKILLS_IMPORT: "/admin/skills/import",
  ADMIN_GIT_MANAGEMENT: "/admin/git-management",
  ADMIN_SYSTEM_INTEGRATIONS: "/admin/system-integrations",
  // CR-040 (SOL-040) — Runtime Agent admin (provision/monitor/revoke the
  // gRPC-connected agent runtime process, distinct from "Agents" = AI
  // persona above).
  ADMIN_RUNTIME_AGENTS: "/admin/runtime-agents",
  ADMIN_RUNTIME_AGENT_NEW: "/admin/runtime-agents/new",
  ADMIN_RUNTIME_AGENT_DETAIL: (id: string) => `/admin/runtime-agents/${id}`,
  ADMIN_CONNECTORS: "/admin/connectors",
  ADMIN_CONNECTORS_HUB: "/admin/connectors/hub",
  ADMIN_BOTS_SLACK: "/admin/bots",
  ADMIN_BOTS_DISCORD: "/admin/discord-bot",
  ADMIN_CONNECTORS_NEW: "/admin/connectors/new",
  // CR-004 — 7-step Google Sheets connector setup wizard, parallel to the
  // generic 2-step ADMIN_CONNECTORS_NEW flow (does not replace it).
  ADMIN_CONNECTORS_NEW_GSHEET: "/admin/connectors/new/gsheet",
  ADMIN_CONNECTOR_DETAIL: (id: string | number) => `/admin/connectors/${id}`,
  ADMIN_SYNC_MONITOR: "/admin/sync-monitor",
  ADMIN_USERS: "/admin/users",
  ADMIN_GROUPS: "/admin/groups",
  ADMIN_SERVICE_ACCOUNTS: "/admin/service-accounts",
  ADMIN_TOKEN_RATE_LIMITS: "/admin/token-rate-limits",
  ADMIN_GOOGLE_QUOTA: "/admin/google-quota",
  ADMIN_TENANTS: "/admin/tenants",
  ADMIN_TENANT_DETAIL: (id: string | number) => `/admin/tenants/${id}`,
  ADMIN_LLM_CONFIG: "/admin/configuration/language-models",
  // CR-025 — Image Generation config. Route existed (router/index.tsx) but
  // had no ROUTES constant and no AdminLayout nav entry — unreachable except
  // by typing the URL directly.
  ADMIN_IMAGE_GEN: "/admin/configuration/image-generation",
  // CR-024 — Voice Mode TTS/STT admin configuration (STT/TTS provider
  // connect/disconnect). Path matches ADMIN_ROUTES.VOICE.path in
  // lib/admin-routes.ts (the not-yet-mounted "Opal" nav tree) so the URL
  // won't change if/when /admin migrates to that tree — same convention as
  // ADMIN_USAGE/ADMIN_QUERY_HISTORY above.
  ADMIN_VOICE_CONFIG: "/admin/configuration/voice",
  // Page components existed (refresh-pages/admin/CodeInterpreterPage,
  // WebSearchPage) but were never registered as routes at all.
  ADMIN_CODE_INTERPRETER: "/admin/configuration/code-interpreter",
  ADMIN_WEB_SEARCH: "/admin/configuration/web-search",
  // CR-034 — Admin Chat Preferences (org-level config, distinct from the
  // per-user "Chat Preferences" tab on /settings). BUG-API-UI5-020.
  ADMIN_CHAT_PREFERENCES: "/admin/configuration/chat-preferences",
  ADMIN_EMBEDDINGS: "/admin/embeddings",
  ADMIN_BILLING: "/admin/billing",
  ADMIN_THEME: "/admin/theme",
  ADMIN_DOCUMENTS: "/admin/documents",
  // TASK-AB003-003 (BUG-AB003-003) — Document Sets admin CRUD, distinct from
  // ADMIN_DOCUMENTS (raw file management) above.
  ADMIN_DOCUMENT_SETS: "/admin/documents/sets",
  // CR-036 — Standard Answers (EE): admin-configured keyword/regex -> answer
  // pairs matched before the LLM call.
  ADMIN_STANDARD_ANSWERS: "/admin/standard-answer",
  // Hook Extensions (EE) — ee/refresh-pages/admin/HooksPage. Page existed,
  // never had a route registered at all.
  ADMIN_HOOKS: "/admin/hooks",
  ADMIN_KG: "/admin/kg",
  // CR-020 — SCIM 2.0 provisioning config. Same gap as ADMIN_IMAGE_GEN above:
  // route existed, no constant, no nav entry.
  ADMIN_SCIM: "/admin/scim",
  // TASK-SA003-001-5 (BUG-SA003-001) — SSO provider configuration (OIDC/SAML).
  ADMIN_SSO: "/admin/sso",
  ADMIN_AUDIT_LOG: "/admin/audit-log",
  // CR-PA (UI-SOL-PA-008) — Provider Agent fleet dashboard, admin
  // selection policy, and the Provider Agent audit log — all three were
  // built (sections/agent/ProviderAgentDashboard.tsx,
  // sections/agent/PolicyConfigPanel.tsx, sections/admin/AuditLogViewer.tsx)
  // but never routed anywhere until now.
  ADMIN_PROVIDER_AGENTS: "/admin/provider-agents",
  // CR-041 (SOL-041) — Session & Key Security admin (KMS session revoke,
  // key-issuance audit log).
  ADMIN_SECURITY_SESSIONS: "/admin/security/sessions",
  ADMIN_SECURITY_KEY_AUDIT_LOG: "/admin/security/key-audit-log",
  // CR-044 (SOL-044 wiring delta, 2026-09-02) — Prompt Firewall / DLP stats
  // dashboard, same "admin/security/*" family as the two routes above.
  ADMIN_SECURITY_PROMPT_FIREWALL: "/admin/security/prompt-firewall",
  // CR-042 (SOL-042 wiring delta, 2026-09-02) — BYOK (Bring Your Own Key)
  // admin panel.
  ADMIN_PRIVACY_BYOK: "/admin/privacy/byok",
  // UI-SOL-PE-016 (v6/privacy-enhance ZKPA, Sprint 8) — Anonymization
  // Pipeline (Emergency Gate + DLP policies) and Differential Privacy
  // budget admin panels, same "admin/privacy/*" family as BYOK above.
  ADMIN_PRIVACY_ANONYMIZATION: "/admin/privacy/anonymization",
  ADMIN_PRIVACY_DP_BUDGET: "/admin/privacy/dp-budget",
  // CR-018 — Analytics & Usage Dashboard. ADMIN_USAGE/ADMIN_QUERY_HISTORY cố
  // ý dùng đúng path string trùng với ADMIN_ROUTES.USAGE.path/
  // ADMIN_ROUTES.QUERY_HISTORY.path (lib/admin-routes.ts, cây "Opal" chưa
  // mount — xem SOL-018 §1.2/§3.5.1) để URL không đổi khi /admin migrate
  // sang AdminSidebar trong tương lai.
  ADMIN_ANALYTICS: "/admin/analytics",
  ADMIN_USAGE: "/admin/performance/usage",
  ADMIN_QUERY_HISTORY: "/admin/performance/query-history",
  // Customer Care v6 (CR-CC-001..010) — route existed since the initial
  // implementation but had no NAV_ITEMS entry, so it was only reachable by
  // typing the URL directly. See SOL-CC-010-admin-nav-and-detail-pages.md.
  ADMIN_CUSTOMER_CARE: "/admin/customer-care",

  // ─── Git Management (CR-GM-000 → CR-GM-010) ─────────────────────────────
  // Tab helper (non-duplicate — ADMIN_GIT_MANAGEMENT and related already defined above)
  ADMIN_GIT_MANAGEMENT_TAB: (tab: string) => `/admin/git-management?tab=${tab}`,

  // ─── VNP-Policy Admin (v8/vnp-policy) — TASK-UI-POL-011 ─────────────────
  ADMIN_POLICY: "/admin/policy",
  ADMIN_POLICY_LIST: "/admin/policy/policies",
  ADMIN_POLICY_NEW: "/admin/policy/policies/new",
  ADMIN_POLICY_EVALUATION: "/admin/policy/evaluation",
  ADMIN_POLICY_COMPLIANCE: "/admin/policy/compliance",
  ADMIN_POLICY_CONFLICTS: "/admin/policy/conflicts",
  ADMIN_POLICY_ENFORCEMENT: "/admin/policy/enforcement",

  // ─── VNP-Proxy Admin (v8/vnp-proxy) — TASK-UI-PRX-011 ───────────────────
  ADMIN_PROXY: "/admin/proxy",
  ADMIN_PROXY_PROVIDERS: "/admin/proxy/providers",
  ADMIN_PROXY_VIRTUAL_KEYS: "/admin/proxy/virtual-keys",
  ADMIN_PROXY_OBSERVABILITY: "/admin/proxy/observability",
  ADMIN_PROXY_MCP_SERVERS: "/admin/proxy/mcp-servers",
  ADMIN_PROXY_FLEET: "/admin/proxy/fleet",
  ADMIN_PROXY_SECURITY: "/admin/proxy/security",

  // ─── VNP-Security Admin (v8/vnp-security) — TASK-UI-SEC-011 ─────────────
  ADMIN_VNP_SECURITY: "/admin/vnp-security",
  ADMIN_VNP_SECURITY_CVE: "/admin/vnp-security/cve",
  ADMIN_VNP_SECURITY_FINDINGS: "/admin/vnp-security/findings",
  ADMIN_VNP_SECURITY_SCAN: "/admin/vnp-security/scan",
  ADMIN_VNP_SECURITY_REPORTS: "/admin/vnp-security/reports",
  ADMIN_VNP_SECURITY_AUDIT: "/admin/vnp-security/audit",

  // ─── Distributed Search (v9) — TASK-UI-DSEARCH-ROUTER ───────────────────
  SEARCH: "/search",
  SEARCH_REQUESTS: "/search/requests",
  SETTINGS_DESKTOP_AGENTS: "/settings/desktop-agents",
  SETTINGS_DESKTOP_AGENT_CONSENT: "/settings/desktop-agent/consent",
  ADMIN_DISTRIBUTED_SEARCH: "/admin/distributed-search",
  ADMIN_DISTRIBUTED_SEARCH_EMBEDDING: "/admin/distributed-search/embedding",

  // ─── Agent Registry (v9/agent-registry) — TASK-UI-AR-016 ────────────────
  ADMIN_AGENT_REGISTRY:          "/admin/agents/registry",
  ADMIN_AGENT_REGISTRY_POLICIES: "/admin/agents/registry/policies",
  ADMIN_AGENT_REGISTRY_DETAIL:   (id: string) => `/admin/agents/registry/${id}`,

  // User-facing agent pages
  SETTINGS_MY_AGENTS:  "/settings/agents",
  SETTINGS_AGENTS_PAIR: "/settings/agents/pair",
  
  // TASK-PERS-003-F-4: Personal Space Settings
  SETTINGS_PERSONAL_LIFECYCLE: "/settings/personal-space/lifecycle",

  // ─── Group 3 Admin Routes (v11) ─────────────────────────────────────────
  ADMIN_DIAGNOSTICS:        "/admin/diagnostics",
  ADMIN_PLUGINS:            "/admin/plugins",
  ADMIN_KMS:                "/admin/kms",
  ADMIN_ENV_MANAGEMENT:     "/admin/env-management",
  ADMIN_WORKSPACE_PRESETS:  "/admin/workspace-presets",
  ADMIN_BRANDING:           "/admin/branding",

  // ─── PIKB Admin UI (CR-PKB-WP-018) ──────────────────────────────────────
  ADMIN_PIKB_ACTOR_ROLES:   "/admin/pikb/actor-roles",
  ADMIN_PIKB_AUDIT:         "/admin/pikb/audit",

  // ─── VNP-DBA Admin Routes (v12 Wave 6 — 2026-09-13) ────────────────────
  ADMIN_VNP_DBA:              "/admin/vnp-dba",
  ADMIN_VNP_DBA_INSTANCES:    "/admin/vnp-dba/instances",
  ADMIN_VNP_DBA_CHANGES:      "/admin/vnp-dba/changes",
  ADMIN_VNP_DBA_SQL:          "/admin/vnp-dba/sql-editor",
  ADMIN_VNP_DBA_POLICIES:     "/admin/vnp-dba/policies",
  ADMIN_VNP_DBA_AUDIT:        "/admin/vnp-dba/audit",

  // ─── OpenPanel Analytics Admin Routes (v12 Wave 6 — 2026-09-13) ─────────
  ADMIN_OPENPANEL:            "/admin/openpanel",
  ADMIN_OPENPANEL_SETTINGS:   "/admin/openpanel/settings",

  // ─── Workspace Routes (CR-WS-006 / CR-WS-011 / CR-WS-031) ────────────────
  WORKSPACE:                  (id: string) => `/ws/${id}`,
  WORKSPACE_CANVAS:           (id: string) => `/ws/${id}/canvas`,
  WORKSPACE_TASKS:            (id: string) => `/ws/${id}/tasks`,
  WORKSPACE_FILES:            (id: string) => `/ws/${id}/files`,
  WORKSPACE_DOCS:             (id: string) => `/ws/${id}/docs`,
  WORKSPACE_MEMBERS:          (id: string) => `/ws/${id}/members`,
  WORKSPACE_MEETINGS:         (id: string) => `/ws/${id}/meetings`,
  WORKSPACE_ARTIFACTS:        (id: string) => `/ws/${id}/artifacts`,
  ARTIFACT:                   (wsId: string, artifactId: string) => `/ws/${wsId}/artifacts/${artifactId}`,
  WORKSPACE_SETTINGS:         (id: string) => `/ws/${id}/settings`,
  WORKSPACE_AGENT:            (id: string) => `/ws/${id}/agent`,
  WORKSPACE_CREATE:           "/ws/new",
  WORKSPACES_NEW:             "/workspaces/new",
  ADMIN_COMPLIANCE:           "/admin/compliance",
  ADMIN_SUBSTRATE_POC:        "/admin/substrate/poc",
  ADMIN_SUBSTRATE_ACTORS:     "/admin/substrate/actors",
  ADMIN_SUBSTRATE_KWP_WORKERS: "/admin/substrate/kwp-workers",
  ADMIN_SUBSTRATE_KWP_AGENTS:  "/admin/substrate/kwp-agents",
  ADMIN_SUBSTRATE_OBSERVABILITY: "/admin/substrate/observability",
  ADMIN_SUBSTRATE_AUDIT:      "/admin/substrate/audit",
  ADMIN_SUBSTRATE_SLA:        "/admin/substrate/sla",
  ADMIN_SUBSTRATE_SECURITY:   "/admin/substrate/security",
  ADMIN_SUBSTRATE_TESTING:    "/admin/substrate/testing",
  ADMIN_SUBSTRATE_QUOTA:      "/admin/substrate/quota",
  ADMIN_SUBSTRATE_CIRCUIT_BREAKERS: "/admin/substrate/circuit-breakers",
  ADMIN_SUBSTRATE_CICD:       "/admin/substrate/cicd",
  ADMIN_SUBSTRATE_SNAPSHOTS:  "/admin/substrate/snapshots",
  ADMIN_SUBSTRATE_AUTOSCALING: "/admin/substrate/autoscaling",
  ADMIN_SUBSTRATE_DEV_EXPERIENCE: "/admin/substrate/dev-experience",
  ADMIN_SUBSTRATE_DATA_RESIDENCY: "/admin/substrate/data-residency",
} as const;

